import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, SectionTitle, Field } from "@/components/ui-hud";
import {
  LayoutDashboard, Users, Fuel, Radio, GraduationCap, DollarSign, Building2,
  Download, Mail, Search, RefreshCw, ChevronDown, ChevronUp, Loader2,
  CheckCircle2, XCircle, Shield, Eye, Pencil, ExternalLink, Trash2, AlertTriangle, FolderOpen,
  Ticket, Copy, Plus, ArrowRight,
} from "lucide-react";

import CommunityDocsPanel from "@/components/CommunityDocsPanel";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "members", label: "Members", icon: Users },
  { key: "fuel", label: "PJP Fuel Fleet", icon: Fuel },
  { key: "fbos", label: "FBOs", icon: Building2 },
  { key: "unicom", label: "UNICOM", icon: Radio },
  { key: "community_docs", label: "Community Docs", icon: FolderOpen },
  { key: "training", label: "Training", icon: GraduationCap },
  { key: "payments", label: "Payments", icon: DollarSign },
  { key: "discount_codes", label: "Comp Codes", icon: Ticket },
  { key: "crew_verifications", label: "Crew Verifications", icon: CheckCircle2 },
  { key: "faa_fixer", label: "FAA Fixer", icon: Shield },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) return <div className="min-h-screen grid place-items-center text-white/40"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <NotAuthorized />;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <SectionTitle overline="// PJP Admin · Platform Control" title="Admin Console" />

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-white/[0.08] pb-3" data-testid="admin-tabbar">
        {TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg mono text-[11px] tracking-widest uppercase transition-all ${
                active
                  ? "bg-sky/15 text-sky border border-sky/40"
                  : "text-white/55 hover:text-white hover:bg-white/5 border border-transparent"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "members" && <MembersTab />}
      {tab === "fuel" && <FuelFleetTab />}
      {tab === "fbos" && <FbosTab />}
      {tab === "unicom" && <UnicomTab />}
      {tab === "community_docs" && <CommunityDocsPanel adminMode />}
      {tab === "training" && <TrainingTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "discount_codes" && <DiscountCodesTab />}
      {tab === "crew_verifications" && <CrewVerificationsTab />}
      {tab === "faa_fixer" && <FaaFixerTab />}
    </div>
  );
}

/* ============================================================
 * Shared building blocks
 * ============================================================ */

function NotAuthorized() {
  return (
    <div className="min-h-[60vh] grid place-items-center px-6">
      <Card className="max-w-md text-center !p-10">
        <Shield className="h-10 w-10 text-sky mx-auto mb-4" />
        <div className="font-display text-2xl font-semibold mb-2">Admin only</div>
        <div className="text-white/60 mb-6">You need PJP admin rights to access this console.</div>
        <Link to="/" className="btn" data-testid="admin-back-home">Back to home</Link>
      </Card>
    </div>
  );
}

function KpiTile({ label, value, sub, tone = "sky", testId }) {
  const toneMap = {
    sky: "text-sky",
    avail: "text-avail",
    danger: "text-aog",
    warn: "text-orange-hud",
    gold: "text-champagne",
  };
  return (
    <div
      data-testid={testId}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hud-corners"
    >
      <span className="hud-c-tl" /><span className="hud-c-br" />
      <div className="mono text-[10px] tracking-[0.22em] text-white/45 uppercase">{label}</div>
      <div className={`font-display text-3xl font-semibold mt-2 ${toneMap[tone] || toneMap.sky}`}>{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
    </div>
  );
}

function MembershipRevenueSection({ mrr }) {
  const totals = mrr.totals || {};
  const tiers = mrr.tiers || [];
  const trend = mrr.charter_client_trend_30d || { daily: [], signups: 0, cancellations: 0, net: 0 };
  const cc = tiers.find((t) => t.tier === "charter_client") || { active: 0, mrr_usd: 0, annualised_usd: 0 };

  const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const stripeMode = mrr.stripe_mode || "test";

  // Sparkline for the 30-day signup trend
  const maxDaily = Math.max(1, ...trend.daily.map((d) => Math.max(d.signups, d.cancellations)));
  const spark = trend.daily.map((d, i) => {
    const y = 24 - Math.round((d.signups / maxDaily) * 20);
    const x = i * (100 / Math.max(1, trend.daily.length - 1));
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y}`;
  }).join(" ");

  return (
    <div data-testid="admin-mrr-section">
      <div className="flex items-baseline justify-between mb-3">
        <div className="eyebrow-muted">Membership Revenue · Live from Stripe</div>
        <div className="mono text-[9px] tracking-widest uppercase text-white/40">
          {stripeMode === "live" ? "LIVE mode" : "TEST mode"}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          testId="kpi-total-mrr"
          label="Total MRR"
          value={money(totals.mrr_usd)}
          tone="avail"
          sub={`${totals.active || 0} active subs`}
        />
        <KpiTile
          testId="kpi-total-arr"
          label="Annualised"
          value={money(totals.annualised_usd)}
          tone="gold"
          sub="Run rate · all tiers"
        />
        <KpiTile
          testId="kpi-cc-mrr"
          label="Charter Client MRR"
          value={money(cc.mrr_usd)}
          tone="sky"
          sub={`${cc.active} × $5k/yr`}
        />
        <KpiTile
          testId="kpi-cc-net-30d"
          label="Charter Client · 30d net"
          value={`${trend.net >= 0 ? "+" : ""}${trend.net}`}
          tone={trend.net >= 0 ? "avail" : "danger"}
          sub={`${trend.signups} new · ${trend.cancellations} cancelled`}
        />
      </div>

      {/* 30-day sparkline */}
      {trend.daily.length > 0 && (
        <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="cc-signup-trend">
          <div className="flex items-baseline justify-between mb-2">
            <div className="mono text-[10px] tracking-[0.22em] text-white/45 uppercase">Charter Client signups · last 30 days</div>
            <div className="mono text-[10px] text-white/40">peak {maxDaily}/day</div>
          </div>
          <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-16">
            <path d={spark} fill="none" stroke="#2ED573" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
            <path d={spark + ` L 100 30 L 0 30 Z`} fill="url(#ccGrad)" opacity="0.25" />
            <defs>
              <linearGradient id="ccGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#2ED573" />
                <stop offset="1" stopColor="#2ED573" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      )}

      {/* Tier breakdown table */}
      <div className="mt-3 rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid="mrr-tier-table">
        <div className="grid grid-cols-[1.5fr_80px_80px_80px_1fr_1fr] gap-3 px-4 py-2 mono text-[10px] tracking-widest uppercase text-white/45 bg-white/[0.03]">
          <div>Tier</div>
          <div className="text-right">Active</div>
          <div className="text-right">Trialing</div>
          <div className="text-right">Past due</div>
          <div className="text-right">MRR</div>
          <div className="text-right">Annualised</div>
        </div>
        {tiers.map((t) => (
          <div key={t.tier} className="grid grid-cols-[1.5fr_80px_80px_80px_1fr_1fr] gap-3 px-4 py-2.5 border-t border-white/[0.04]" data-testid={`mrr-row-${t.tier}`}>
            <div className="text-white/85 capitalize">{t.tier.replace(/_/g, " ")}</div>
            <div className="mono text-avail text-right">{t.active}</div>
            <div className="mono text-white/60 text-right">{t.trialing}</div>
            <div className="mono text-orange-hud text-right">{t.past_due}</div>
            <div className="mono text-white text-right">{money(t.mrr_usd)}</div>
            <div className="mono text-champagne text-right">{money(t.annualised_usd)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExportBar({ endpoint, filename, disabled }) {
  const [emailing, setEmailing] = useState(false);

  const csvUrl = (() => {
    const sep = endpoint.includes("?") ? "&" : "?";
    return `${API_BASE}/api${endpoint}${sep}format=csv`;
  })();

  const downloadCsv = async () => {
    // Authorized fetch → blob → save-as
    try {
      const tok = localStorage.getItem("pjp_token");
      const res = await fetch(csvUrl, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const emailCsv = async () => {
    if (emailing) return;
    setEmailing(true);
    try {
      const sep = endpoint.includes("?") ? "&" : "?";
      const r = await api.get(`${endpoint}${sep}format=email`);
      if (r.data?.ok) toast.success(`Emailed ${r.data.row_count || 0} rows to ${r.data.emailed_to}`);
      else toast.success("Export queued");
    } catch (e) {
      toast.error("Email export failed");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={downloadCsv}
        disabled={disabled}
        data-testid="btn-export-csv"
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 mono text-[10px] tracking-widest text-sky border border-sky/40 hover:bg-sky/10 transition-colors disabled:opacity-50"
      >
        <Download className="h-3 w-3" /> CSV
      </button>
      <button
        onClick={emailCsv}
        disabled={disabled || emailing}
        data-testid="btn-export-email"
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 mono text-[10px] tracking-widest text-white/70 border border-white/15 hover:bg-white/5 transition-colors disabled:opacity-50"
      >
        {emailing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} EMAIL ME
      </button>
    </div>
  );
}

function AdminTable({ columns, rows, empty = "No records", testId }) {
  if (!rows || rows.length === 0) {
    return <div className="py-14 text-center text-white/40 mono text-[11px] tracking-widest">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto -mx-2" data-testid={testId}>
      <table className="w-full min-w-[720px]">
        <thead>
          <tr className="border-b border-white/[0.08]">
            {columns.map((c) => (
              <th key={c.key} className="text-left mono text-[10px] tracking-[0.18em] text-white/50 uppercase px-3 py-2 whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || row._key || i} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2.5 align-top text-[13px] text-white/85">
                  {c.render ? c.render(row) : String(row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SearchBox({ value, onChange, placeholder = "Search…", testId }) {
  return (
    <div className="relative flex-1 min-w-[220px] max-w-md">
      <Search className="h-4 w-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/[0.03] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-sky/60"
      />
    </div>
  );
}

/* ============================================================
 * OVERVIEW
 * ============================================================ */

function OverviewTab() {
  const [data, setData] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/mrr").catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setMrr(m.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="py-16 grid place-items-center text-white/40"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow mb-1">Live Snapshot</div>
          <div className="mono text-[11px] text-white/40">Refreshed {new Date(data.generated_at).toLocaleString()}</div>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 mono text-[10px] tracking-widest text-sky border border-sky/40 hover:bg-sky/10">
          <RefreshCw className="h-3 w-3" /> REFRESH
        </button>
      </div>

      <div>
        <div className="eyebrow-muted mb-3">Members</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiTile testId="kpi-members-total" label="Total accounts" value={data.members.total.toLocaleString()} />
          <KpiTile testId="kpi-members-faa" label="FAA-verified" value={data.members.faa_verified.toLocaleString()} tone="avail" sub={data.members.total ? `${Math.round(100 * data.members.faa_verified / data.members.total)}% of accounts` : ""} />
          <KpiTile testId="kpi-members-active" label="Active last 30d" value={data.members.active_30d.toLocaleString()} tone="gold" />
        </div>
      </div>

      {mrr && (
        <MembershipRevenueSection mrr={mrr} />
      )}

      <div>
        <div className="eyebrow-muted mb-3">PJP Fuel</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiTile testId="kpi-fuel-active" label="Active tails" value={data.fuel.tails_active.toLocaleString()} tone="sky" />
          <KpiTile testId="kpi-fuel-total" label="Total registered" value={data.fuel.tails_total.toLocaleString()} />
          <KpiTile testId="kpi-fuel-mrr" label="Est. MRR" value={`$${data.fuel.mrr_estimate_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="avail" sub="Based on $499/yr per active tail" />
        </div>
      </div>

      <div>
        <div className="eyebrow-muted mb-3">Ops</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile testId="kpi-contracts-open" label="Open contracts" value={data.ops.contracts_open.toLocaleString()} />
          <KpiTile testId="kpi-aog-open" label="AOG open" value={data.ops.aog_open.toLocaleString()} tone="danger" />
          <KpiTile testId="kpi-unicom-open" label="UNICOM open" value={data.ops.unicom_open.toLocaleString()} tone="warn" />
          <KpiTile testId="kpi-unicom-total" label="UNICOM total" value={data.ops.unicom_total.toLocaleString()} />
        </div>
      </div>

      <div>
        <div className="eyebrow-muted mb-3">FBOs & Social</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile testId="kpi-fbos-total" label="FBOs listed" value={data.fbos.total.toLocaleString()} />
          <KpiTile testId="kpi-fbos-claimed" label="Claimed by FBO ops" value={data.fbos.claimed.toLocaleString()} tone="avail" />
          <KpiTile testId="kpi-social-total" label="Social posts (all-time)" value={data.social.posts_total.toLocaleString()} />
          <KpiTile testId="kpi-social-24h" label="Posts last 24h" value={data.social.posts_24h.toLocaleString()} tone="gold" />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * MEMBERS
 * ============================================================ */
// Backend role keys → user-facing labels. "enthusiast" (and other executive-adjacent
// roles) surface as "AV/EXEC" per Jeff's rebrand.
const ROLE_LABELS = {
  pilot: "PILOT",
  flight_attendant: "FLIGHT ATTENDANT",
  amt: "A&P TECHNICIAN",
  mechanic: "A&P TECHNICIAN",
  fbo: "FBO MANAGER",
  charter_operator: "CHARTER OPERATOR",
  operator: "CHARTER OPERATOR",
  aircraft_owner: "AV/EXEC",
  aircraft_sales: "AV/EXEC",
  broker: "AV/EXEC",
  enthusiast: "AV/EXEC",
  admin: "PJP ADMIN",
};
const roleLabel = (r) => ROLE_LABELS[(r || "").toLowerCase()] || (r || "—").replace(/_/g, " ").toUpperCase();
const ROLE_OPTIONS = ["", "pilot", "flight_attendant", "amt", "mechanic", "fbo", "charter_operator", "aircraft_owner", "aircraft_sales", "broker", "operator", "enthusiast", "admin"];
const MEMBERSHIP_OPTIONS = ["", "active", "pending", "suspended", "cancelled", "trialing"];

function MembersTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [pjpIdStatus, setPjpIdStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (role) params.set("role", role);
      if (status) params.set("status", status);
      if (pjpIdStatus) params.set("pjp_id_status", pjpIdStatus);
      params.set("limit", "200");
      const r = await api.get(`/admin/members?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [q, role, status, pjpIdStatus]);

  useEffect(() => { load(); }, [load]);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (role) params.set("role", role);
    if (status) params.set("status", status);
    if (pjpIdStatus) params.set("pjp_id_status", pjpIdStatus);
    return `/admin/members${params.toString() ? `?${params.toString()}` : ""}`;
  }, [q, role, status, pjpIdStatus]);

  return (
    <div className="space-y-4" data-testid="admin-members">
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <SearchBox value={q} onChange={setQ} placeholder="Name, email, cert #, home base…" testId="admin-members-search" />
          <select data-testid="admin-members-role" value={role} onChange={(e) => setRole(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky/60">
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r ? roleLabel(r) : "All roles"}</option>)}
          </select>
          <select data-testid="admin-members-status" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky/60">
            {MEMBERSHIP_OPTIONS.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <select
            data-testid="admin-members-pjpid"
            value={pjpIdStatus}
            onChange={(e) => setPjpIdStatus(e.target.value)}
            className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky/60"
            title="Filter by Digital ID / Crew ID purchase status"
          >
            <option value="">Any Digital ID</option>
            <option value="active">✓ Has Digital ID</option>
            <option value="none">✗ No Digital ID</option>
          </select>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-white/70 hover:text-white hover:bg-white/5" data-testid="admin-members-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
          <div className="ml-auto flex items-center gap-3">
            <div className="mono text-[10px] text-white/40 tracking-widest">{rows.length} / {total.toLocaleString()} shown</div>
            <ExportBar endpoint={endpoint} filename="pjp_members.csv" disabled={loading} />
          </div>
        </div>
        <AdminTable
          testId="admin-members-table"
          columns={[
            { key: "name", label: "Name", render: (r) => (<div><div className="text-white font-medium">{r.first_name} {r.last_name}</div><div className="mono text-[10px] text-white/40">{r.email}</div></div>) },
            { key: "role", label: "Role", render: (r) => <Badge tone={r.role === "admin" ? "gold" : "neutral"}>{r.role_label || roleLabel(r.role)}</Badge> },
            { key: "membership_status", label: "Status", render: (r) => <Badge tone={r.membership_status === "active" ? "avail" : "neutral"}>{r.membership_status || "—"}</Badge> },
            { key: "faa_verified", label: "FAA", render: (r) => r.faa_verified ? <CheckCircle2 className="h-4 w-4 text-avail" /> : <XCircle className="h-4 w-4 text-white/25" /> },
            { key: "pjp_id_status", label: "Digital ID", render: (r) => {
              const active = r.pjp_id_status === "active";
              const src = r.pjp_id_grant_source || "";
              const isFree = src.includes("founding") || src.includes("migration") || src.includes("comp") || r.founding_member;
              if (active) {
                return (
                  <Badge tone={isFree ? "gold" : "avail"} title={src || "Purchased"}>
                    {isFree ? "GRANT" : "PAID"}
                  </Badge>
                );
              }
              return <span className="mono text-[10px] text-white/30">—</span>;
            } },
            { key: "home_base", label: "Home Base", render: (r) => <span className="mono text-[11px] text-sky">{r.home_base || "—"}</span> },
            { key: "renewal", label: "Renewal", render: (r) => <span className="mono text-[10px] text-white/45">{r.subscription_current_period_end ? new Date(r.subscription_current_period_end).toLocaleDateString() : "—"}</span> },
            { key: "created_at", label: "Joined", render: (r) => <span className="mono text-[10px] text-white/45">{(r.created_at || "").slice(0, 10)}</span> },
            { key: "actions", label: "", render: (r) => (
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setEditing(r)} data-testid={`admin-edit-${r.id}`} className="inline-flex items-center gap-1 mono text-[10px] tracking-widest text-sky hover:text-white">
                  <Pencil className="h-3 w-3" /> EDIT
                </button>
                <button onClick={() => setDeleting(r)} data-testid={`admin-delete-${r.id}`} className="inline-flex items-center gap-1 mono text-[10px] tracking-widest text-red-400 hover:text-red-300">
                  <Trash2 className="h-3 w-3" /> DELETE
                </button>
              </div>
            ) },
          ]}
          rows={rows}
          empty="No members match filters"
        />
      </Card>

      {editing && <MemberEditor member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {deleting && <DeleteMemberDialog member={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />}
    </div>
  );
}

function DeleteMemberDialog({ member, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const canDelete = confirm.trim().toLowerCase() === (member.email || "").toLowerCase();

  const del = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const r = await api.delete(`/admin/members/${member.id}`);
      const purged = r?.data?.purged || {};
      const summary = Object.entries(purged).filter(([k]) => k !== "users").map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Deleted ${member.first_name || member.email}${summary ? ` (${summary})` : ""}`);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm grid place-items-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-md max-h-[calc(100vh-3rem)] overflow-y-auto border-red-500/40 my-auto" onClick={(e) => e.stopPropagation()} testId="admin-delete-dialog">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-full bg-red-500/15 grid place-items-center border border-red-500/40">
            <AlertTriangle className="h-4 w-4 text-red-400" />
          </div>
          <div className="flex-1">
            <div className="eyebrow text-red-400">// DELETE MEMBER · IRREVERSIBLE</div>
            <div className="font-display text-lg mt-1">{member.first_name} {member.last_name}</div>
            <div className="mono text-[10px] text-white/45">{member.email}</div>
          </div>
        </div>
        <p className="text-sm text-white/70 mb-3">
          This will permanently delete their account and all associated data — profile, aircraft, marketplace listings,
          contracts, applications, forum activity, documents, payment history, and cancel any active subscription. This
          <span className="text-red-400"> cannot be undone.</span>
        </p>
        <Field label={<>Type <span className="mono text-red-400">{member.email}</span> to confirm</>}>
          <input
            data-testid="admin-delete-confirm"
            className="w-full bg-white/[0.03] border border-red-500/40 rounded-lg px-3 py-2 text-sm text-white font-mono"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoFocus
          />
        </Field>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost" data-testid="admin-delete-cancel">Cancel</button>
          <button
            onClick={del}
            disabled={!canDelete || busy}
            data-testid="admin-delete-confirm-btn"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-red-500/90 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Permanently delete
          </button>
        </div>
      </Card>
    </div>
  );
}

function MemberEditor({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    role: member.role || "",
    membership_status: member.membership_status || "",
    is_admin: !!member.is_admin,
    faa_verified: !!member.faa_verified,
    banned: !!member.banned,
    notes_by_admin: member.notes_by_admin || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/members/${member.id}`, form);
      toast.success("Member updated");
      onSaved && onSaved();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">// Edit Member</div>
            <div className="font-display text-xl">{member.first_name} {member.last_name}</div>
            <div className="mono text-[10px] text-white/45">{member.email}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="admin-edit-close">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              {ROLE_OPTIONS.filter(Boolean).map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </Field>
          <Field label="Membership status">
            <select value={form.membership_status} onChange={(e) => setForm({ ...form, membership_status: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              {MEMBERSHIP_OPTIONS.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <label className="flex items-center gap-2 text-sm text-white/85"><input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} /> Admin</label>
          <label className="flex items-center gap-2 text-sm text-white/85"><input type="checkbox" checked={form.faa_verified} onChange={(e) => setForm({ ...form, faa_verified: e.target.checked })} /> FAA-verified</label>
          <label className="flex items-center gap-2 text-sm text-white/85"><input type="checkbox" checked={form.banned} onChange={(e) => setForm({ ...form, banned: e.target.checked })} /> Banned</label>
        </div>
        <Field label="Admin notes"><textarea value={form.notes_by_admin} onChange={(e) => setForm({ ...form, notes_by_admin: e.target.value })} rows={3} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
        <DigitalIdPanel member={member} />
        <MemberDocsPanel userId={member.id} />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost" data-testid="admin-edit-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn" data-testid="admin-edit-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </Card>
    </div>
  );
}

/* ============================================================
 * PJP FUEL FLEET
 * ============================================================ */

function FuelFleetTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      const r = await api.get(`/admin/fuel-fleet?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setActive(r.data.active || 0);
    } finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const endpoint = `/admin/fuel-fleet${q || status ? `?${new URLSearchParams({ ...(q ? { q } : {}), ...(status ? { status } : {}) }).toString()}` : ""}`;

  return (
    <div className="space-y-4" data-testid="admin-fuel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Active tails" value={active.toLocaleString()} tone="sky" testId="kpi-fuel-active2" />
        <KpiTile label="Total tails" value={total.toLocaleString()} testId="kpi-fuel-total2" />
        <KpiTile label="Est. MRR" value={`$${(active * 41.58).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="avail" testId="kpi-fuel-mrr2" />
        <KpiTile label="Est. Annual" value={`$${(active * 499).toLocaleString()}`} tone="gold" testId="kpi-fuel-arr" />
      </div>
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <SearchBox value={q} onChange={setQ} placeholder="Tail #, aircraft type, home base…" testId="admin-fuel-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-fuel-status" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="">All statuses</option>
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="cancelled">cancelled</option>
          </select>
          <div className="ml-auto"><ExportBar endpoint={endpoint} filename="pjp_fuel_fleet.csv" /></div>
        </div>
        <AdminTable
          testId="admin-fuel-table"
          columns={[
            { key: "tail_number", label: "Tail #", render: (r) => <span className="mono text-white font-bold">{r.tail_number || "—"}</span> },
            { key: "aircraft_type", label: "Aircraft" },
            { key: "fuel_type", label: "Fuel", render: (r) => <Badge tone="neutral">{r.fuel_type || "—"}</Badge> },
            { key: "home_base", label: "Home", render: (r) => <span className="mono text-sky">{r.home_base || "—"}</span> },
            { key: "owner", label: "Owner", render: (r) => (<div><div>{r.owner_name || "—"}</div><div className="mono text-[10px] text-white/45">{r.owner_email || ""}</div></div>) },
            { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "active" ? "avail" : "neutral"}>{r.status || "—"}</Badge> },
            { key: "renewal_at", label: "Renews", render: (r) => <span className="mono text-[11px] text-white/60">{(r.renewal_at || "").slice(0, 10) || "—"}</span> },
          ]}
          rows={rows}
          empty="No tails registered yet"
        />
      </Card>
    </div>
  );
}

/* ============================================================
 * FBOs
 * ============================================================ */

function FbosTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [claimed, setClaimed] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (claimed !== "") params.set("claimed", claimed);
      const r = await api.get(`/admin/fbos?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  }, [q, claimed]);
  useEffect(() => { load(); }, [load]);

  const endpoint = `/admin/fbos${q || claimed !== "" ? `?${new URLSearchParams({ ...(q ? { q } : {}), ...(claimed !== "" ? { claimed } : {}) }).toString()}` : ""}`;

  return (
    <div className="space-y-4" data-testid="admin-fbos">
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <SearchBox value={q} onChange={setQ} placeholder="FBO name, handle, ICAO, email…" testId="admin-fbos-search" />
          <select value={claimed} onChange={(e) => setClaimed(e.target.value)} data-testid="admin-fbos-claimed" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="">All</option>
            <option value="true">Claimed</option>
            <option value="false">Unclaimed</option>
          </select>
          <div className="ml-auto flex items-center gap-3">
            <div className="mono text-[10px] text-white/40 tracking-widest">{rows.length} / {total.toLocaleString()} shown</div>
            <ExportBar endpoint={endpoint} filename="pjp_fbos.csv" />
          </div>
        </div>
        <AdminTable
          testId="admin-fbos-table"
          columns={[
            { key: "name", label: "Name", render: (r) => (<div><div className="text-white font-medium">{r.name}</div><div className="mono text-[10px] text-white/40">@{r.handle}</div></div>) },
            { key: "icao", label: "ICAO", render: (r) => <span className="mono text-sky">{r.icao || "—"}</span> },
            { key: "email", label: "Email" },
            { key: "fuel_100ll_price", label: "100LL", render: (r) => r.fuel_100ll_price ? <span className="mono text-white">${r.fuel_100ll_price.toFixed(2)}</span> : <span className="text-white/30">—</span> },
            { key: "fuel_jeta_price", label: "Jet A", render: (r) => r.fuel_jeta_price ? <span className="mono text-white">${r.fuel_jeta_price.toFixed(2)}</span> : <span className="text-white/30">—</span> },
            { key: "claimed", label: "Claimed", render: (r) => r.claimed_by_user_id ? <CheckCircle2 className="h-4 w-4 text-avail" /> : <XCircle className="h-4 w-4 text-white/25" /> },
            { key: "actions", label: "", render: (r) => <button onClick={() => setEditing(r)} data-testid={`admin-fbo-edit-${r.id}`} className="mono text-[10px] tracking-widest text-sky hover:text-white"><Pencil className="h-3 w-3 inline mr-1" />EDIT</button> },
          ]}
          rows={rows}
        />
      </Card>
      {editing && <FboEditor fbo={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FboEditor({ fbo, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: fbo.name || "",
    handle: fbo.handle || "",
    icao: fbo.icao || "",
    email: fbo.email || "",
    phone: fbo.phone || "",
    fuel_100ll_price: fbo.fuel_100ll_price || "",
    fuel_jeta_price: fbo.fuel_jeta_price || "",
    services: (fbo.services || []).join(", "),
    notes_by_admin: fbo.notes_by_admin || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        ...form,
        fuel_100ll_price: form.fuel_100ll_price ? parseFloat(form.fuel_100ll_price) : null,
        fuel_jeta_price: form.fuel_jeta_price ? parseFloat(form.fuel_jeta_price) : null,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.patch(`/admin/fbos/${fbo.id}`, patch);
      toast.success("FBO updated");
      onSaved && onSaved();
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center px-4" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">// Edit FBO</div>
            <div className="font-display text-xl">{fbo.name}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
          <Field label="Handle"><input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
          <Field label="ICAO"><input value={form.icao} onChange={(e) => setForm({ ...form, icao: e.target.value.toUpperCase() })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white uppercase" /></Field>
          <Field label="Email"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
          <div />
          <Field label="100LL price $/gal"><input value={form.fuel_100ll_price} onChange={(e) => setForm({ ...form, fuel_100ll_price: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
          <Field label="Jet A price $/gal"><input value={form.fuel_jeta_price} onChange={(e) => setForm({ ...form, fuel_jeta_price: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
        </div>
        <Field label="Services (comma separated)"><input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
        <Field label="Admin notes"><textarea rows={2} value={form.notes_by_admin} onChange={(e) => setForm({ ...form, notes_by_admin: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" /></Field>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      </Card>
    </div>
  );
}

/* ============================================================
 * UNICOM
 * ============================================================ */

function UnicomTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      const r = await api.get(`/admin/unicom?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const endpoint = `/admin/unicom${q || status ? `?${new URLSearchParams({ ...(q ? { q } : {}), ...(status ? { status } : {}) }).toString()}` : ""}`;

  return (
    <div className="space-y-4" data-testid="admin-unicom">
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <SearchBox value={q} onChange={setQ} placeholder="Subject, body, ICAO, tail…" testId="admin-unicom-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-unicom-status" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="">All statuses</option>
            <option value="open">open</option>
            <option value="pending">pending</option>
            <option value="closed">closed</option>
          </select>
          <div className="ml-auto flex items-center gap-3">
            <div className="mono text-[10px] text-white/40 tracking-widest">{rows.length} / {total.toLocaleString()} shown</div>
            <ExportBar endpoint={endpoint} filename="pjp_unicom.csv" />
          </div>
        </div>
        <AdminTable
          testId="admin-unicom-table"
          columns={[
            { key: "created_at", label: "When", render: (r) => <span className="mono text-[11px] text-white/60">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span> },
            { key: "icao", label: "ICAO", render: (r) => <span className="mono text-sky">{r.icao || "—"}</span> },
            { key: "tail_number", label: "Tail", render: (r) => <span className="mono">{r.tail_number || "—"}</span> },
            { key: "subject", label: "Subject", render: (r) => <div className="max-w-xs truncate text-white/90" title={r.subject}>{r.subject || "—"}</div> },
            { key: "requester_email", label: "From", render: (r) => <span className="mono text-[11px]">{r.requester_email || "—"}</span> },
            { key: "services", label: "Services", render: (r) => (r.services || []).slice(0, 2).map((s, i) => <Badge key={i} tone="neutral">{s}</Badge>) },
            { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "open" ? "avail" : r.status === "closed" ? "neutral" : "gold"}>{r.status || "—"}</Badge> },
          ]}
          rows={rows}
        />
      </Card>
    </div>
  );
}

/* ============================================================
 * TRAINING
 * ============================================================ */

function TrainingTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [within, setWithin] = useState(180);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ within_days: String(within) });
      if (role) params.set("role", role);
      const r = await api.get(`/admin/training?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  }, [within, role]);
  useEffect(() => { load(); }, [load]);

  const endpoint = `/admin/training?within_days=${within}${role ? `&role=${role}` : ""}`;

  return (
    <div className="space-y-4" data-testid="admin-training">
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="mono text-[10px] text-white/45 tracking-widest">WITHIN</span>
            <select value={within} onChange={(e) => setWithin(Number(e.target.value))} data-testid="admin-training-within" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days</option>
            </select>
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="admin-training-role">
            <option value="">All roles</option>
            <option value="pilot">Pilot</option>
            <option value="flight_attendant">Flight Attendant</option>
            <option value="amt">A&P Mechanic</option>
          </select>
          <div className="ml-auto flex items-center gap-3">
            <div className="mono text-[10px] text-white/40 tracking-widest">{total.toLocaleString()} rows</div>
            <ExportBar endpoint={endpoint} filename="pjp_training.csv" />
          </div>
        </div>
        <AdminTable
          testId="admin-training-table"
          columns={[
            { key: "name", label: "Member", render: (r) => (<div><div className="text-white font-medium">{r.name}</div><div className="mono text-[10px] text-white/40">{r.email}</div></div>) },
            { key: "role", label: "Role", render: (r) => <Badge tone="neutral">{r.role}</Badge> },
            { key: "training_kind", label: "Kind" },
            { key: "subject", label: "Subject", render: (r) => <span className="mono text-sky">{r.subject || "—"}</span> },
            { key: "due_at", label: "Due", render: (r) => <span className="mono text-[11px]">{(r.due_at || "").slice(0, 10)}</span> },
            { key: "days_until_due", label: "Days left", render: (r) => <Badge tone={r.days_until_due < 30 ? "danger" : r.days_until_due < 90 ? "gold" : "neutral"}>{r.days_until_due}d</Badge> },
          ]}
          rows={rows}
          empty="No upcoming training within window"
        />
      </Card>
    </div>
  );
}

/* ============================================================
 * PAYMENTS
 * ============================================================ */

function PaymentsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ paid_cents: 0, pending_cents: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payments");
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setTotals(r.data.totals || {});
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmtUsd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4" data-testid="admin-payments">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiTile label="Total transactions" value={total.toLocaleString()} testId="kpi-pmt-total" />
        <KpiTile label="Paid" value={fmtUsd(totals.paid_cents)} tone="avail" testId="kpi-pmt-paid" />
        <KpiTile label="Pending" value={fmtUsd(totals.pending_cents)} tone="gold" testId="kpi-pmt-pending" />
      </div>
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">Recent Stripe transactions</div>
          <ExportBar endpoint="/admin/payments" filename="pjp_payments.csv" />
        </div>
        <AdminTable
          testId="admin-payments-table"
          columns={[
            { key: "created_at", label: "When", render: (r) => <span className="mono text-[11px] text-white/60">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span> },
            { key: "kind", label: "Kind" },
            { key: "user_email", label: "Member", render: (r) => r.user_email || r.user_id || "—" },
            { key: "amount", label: "Amount", render: (r) => <span className="mono text-white">{fmtUsd(r.amount_cents)}</span> },
            { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "paid" ? "avail" : r.status === "pending" ? "gold" : "danger"}>{r.status}</Badge> },
            { key: "stripe_session_id", label: "Stripe", render: (r) => r.stripe_session_id ? <span className="mono text-[10px] text-sky truncate max-w-[160px] inline-block">{r.stripe_session_id}</span> : "—" },
          ]}
          rows={rows}
        />
      </Card>
    </div>
  );
}

/* ============================================================
 * DISCOUNT / COMP CODES
 * Owner-issued secret codes that bypass Stripe on signup and grant a
 * membership tier (default: Pro) for a duration (default: 365 days).
 * ============================================================ */

const TIER_GRANT_OPTIONS = [
  { slug: "pjp_pro", label: "PJP Pro — full pilot access" },
  { slug: "charter_operator", label: "Charter Operator" },
  { slug: "charter_client", label: "Charter Client — $5k tier" },
  { slug: "empty_leg_rider", label: "Empty Leg Rider — $10k tier" },
];

function DiscountCodesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/discount-codes");
      setRows(r.data.codes || []);
    } catch {
      toast.error("Failed to load codes");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied ${code} to clipboard`);
  };

  const toggleActive = async (code, active) => {
    try {
      await api.patch(`/admin/discount-codes/${code}`, { active: !active });
      toast.success(`${code} ${active ? "disabled" : "enabled"}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete code ${code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/discount-codes/${code}`);
      toast.success(`${code} deleted`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-discount-codes">
      <Card>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="eyebrow">// Secret comp codes</div>
            <div className="text-[12.5px] text-white/60 max-w-2xl mt-1">
              Issue a code to bypass Stripe and give a chosen pilot Pro access for a fixed window.
              Codes are one-time by default and grow through <code className="mono text-sky text-[11px]">/signup</code> — no payment required.
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="admin-dc-new"
            className="btn-hud btn-hud-solid"
          >
            <Plus className="h-3.5 w-3.5" /> NEW CODE
          </button>
        </div>
        <AdminTable
          testId="admin-dc-table"
          columns={[
            { key: "code", label: "Code", render: (r) => (
              <div className="flex items-center gap-2">
                <span className="mono text-white font-bold tracking-wider">{r.code}</span>
                <button onClick={() => copyCode(r.code)} data-testid={`admin-dc-copy-${r.code}`} className="text-white/45 hover:text-sky" title="Copy">
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            ) },
            { key: "tier_grant", label: "Grants", render: (r) => <Badge tone="avail">{(r.tier_grant || "pjp_pro").replace(/_/g, " ").toUpperCase()}</Badge> },
            { key: "duration_days", label: "Duration", render: (r) => <span className="mono text-[11px] text-white/85">{r.duration_days} days</span> },
            { key: "uses", label: "Uses", render: (r) => <span className="mono text-[11px]">{r.uses_count}{r.uses_max ? ` / ${r.uses_max}` : " / ∞"}</span> },
            { key: "active", label: "Status", render: (r) => (
              <button
                onClick={() => toggleActive(r.code, r.active)}
                data-testid={`admin-dc-toggle-${r.code}`}
                className={`mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${
                  r.active
                    ? "text-avail border-avail/40 bg-avail/10 hover:bg-avail/20"
                    : "text-white/40 border-white/15 bg-white/[0.03] hover:bg-white/10"
                }`}
              >
                {r.active ? "ACTIVE" : "DISABLED"}
              </button>
            ) },
            { key: "notes", label: "Notes", render: (r) => <span className="text-[12px] text-white/70">{r.notes || "—"}</span> },
            { key: "created_at", label: "Created", render: (r) => <span className="mono text-[10px] text-white/45">{(r.created_at || "").slice(0, 10)}</span> },
            { key: "actions", label: "", render: (r) => (
              <button onClick={() => deleteCode(r.code)} data-testid={`admin-dc-delete-${r.code}`} className="mono text-[10px] tracking-widest text-red-400 hover:text-red-300">
                <Trash2 className="h-3 w-3 inline mr-1" /> DELETE
              </button>
            ) },
          ]}
          rows={rows}
          empty={loading ? "Loading…" : "No codes issued yet — create one to give a pilot free Pro access."}
        />
      </Card>
      {showCreate && (
        <NewCodeDialog
          onClose={() => setShowCreate(false)}
          onCreated={(code) => { setShowCreate(false); load(); toast.success(`Code ${code.code} ready to share`); }}
        />
      )}
    </div>
  );
}

function NewCodeDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "",
    tier_grant: "pjp_pro",
    duration_days: 365,
    uses_max: 1,
    unlimited: false,
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim() || null,
        tier_grant: form.tier_grant,
        duration_days: Number(form.duration_days) || 365,
        uses_max: form.unlimited ? null : (Number(form.uses_max) || 1),
        notes: form.notes.trim() || null,
      };
      const r = await api.post("/admin/discount-codes", payload);
      onCreated && onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create code");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm grid place-items-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-lg my-auto" onClick={(e) => e.stopPropagation()} testId="admin-dc-new-dialog">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="eyebrow text-avail">// NEW COMP CODE</div>
            <div className="font-display text-xl mt-1">Give a pilot a free ride</div>
            <div className="text-[12px] text-white/55 mt-1">They'll bypass Stripe and get instant Pro access when they enter this on signup.</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="admin-dc-new-close">✕</button>
        </div>
        <Field label="Code (leave blank to auto-generate)">
          <input
            data-testid="admin-dc-form-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="PJP-JORDEN-2026"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono tracking-wider focus:outline-none focus:border-avail/60"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Grants tier">
            <select
              data-testid="admin-dc-form-tier"
              value={form.tier_grant}
              onChange={(e) => setForm({ ...form, tier_grant: e.target.value })}
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              {TIER_GRANT_OPTIONS.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Duration (days)">
            <input
              data-testid="admin-dc-form-duration"
              type="number"
              value={form.duration_days}
              onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            />
          </Field>
        </div>
        <Field label="Max uses">
          <div className="flex items-center gap-3">
            <input
              data-testid="admin-dc-form-uses"
              type="number"
              disabled={form.unlimited}
              value={form.uses_max}
              onChange={(e) => setForm({ ...form, uses_max: e.target.value })}
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-40"
            />
            <label className="inline-flex items-center gap-2 text-sm text-white/80 mono text-[11px]">
              <input
                data-testid="admin-dc-form-unlimited"
                type="checkbox"
                checked={form.unlimited}
                onChange={(e) => setForm({ ...form, unlimited: e.target.checked })}
              />
              UNLIMITED
            </label>
          </div>
        </Field>
        <Field label="Internal notes (who / why)">
          <input
            data-testid="admin-dc-form-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Jorden — brother, free year"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          />
        </Field>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost" data-testid="admin-dc-form-cancel">Cancel</button>
          <button
            onClick={create}
            disabled={busy}
            data-testid="admin-dc-form-create"
            className="btn-hud btn-hud-solid"
            style={{ boxShadow: "0 0 20px rgba(46,213,115,0.45)" }}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Ticket className="h-3.5 w-3.5" /> CREATE CODE</>}
          </button>
        </div>
      </Card>
    </div>
  );
}


/* ============================================================
 * FAA FIXER
 * Emergency tool for helping a pilot whose FAA cross-check keeps
 * failing (alternate spellings, married-name change, missing from
 * imported dataset, etc). Search for the pilot's PJP account →
 * search FAA broadly by any name → pick the match → force-verify.
 * ============================================================ */
function FaaFixerTab() {
  const [userQuery, setUserQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState(null);
  const [faaLast, setFaaLast] = useState("");
  const [faaFirst, setFaaFirst] = useState("");
  const [faaResults, setFaaResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bypassReason, setBypassReason] = useState("");

  // ═══ Data freshness ═══
  const [importStatus, setImportStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/faa/import-status");
      setImportStatus(r.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const importNow = async () => {
    if (!window.confirm("Kick off a full FAA import? This downloads the latest FAA airman ZIP and rebuilds the pilot index (~2 min).")) return;
    setImporting(true);
    try {
      await api.post("/admin/faa/import-now");
      toast.success("Import running in background — refresh the freshness card in ~2 min.");
      setTimeout(loadStatus, 10000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed to kick off"); }
    finally { setImporting(false); }
  };

  const searchUsers = async () => {
    if (userQuery.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await api.get(`/admin/users/search?q=${encodeURIComponent(userQuery.trim())}`);
      setUsers(r.data.results || []);
    } catch { toast.error("User search failed"); }
    finally { setBusy(false); }
  };

  const searchFaa = async () => {
    if (faaLast.trim().length < 3) return;
    setBusy(true);
    setFaaResults(null);
    try {
      const params = new URLSearchParams({ last: faaLast.trim() });
      if (faaFirst.trim()) params.set("first", faaFirst.trim());
      const r = await api.get(`/admin/faa/search?${params.toString()}`);
      setFaaResults(r.data.results || []);
    } catch { toast.error("FAA search failed"); }
    finally { setBusy(false); }
  };

  const attachMatch = async (unique_id) => {
    if (!picked) return;
    if (!window.confirm(`Attach FAA record ${unique_id} to ${picked.first_name} ${picked.last_name} and mark verified?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${picked.id}/faa-force-verify`, { unique_id });
      toast.success(`${picked.first_name} is now FAA-verified.`);
      setPicked(null); setFaaResults(null); setFaaFirst(""); setFaaLast("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Force-verify failed"); }
    finally { setBusy(false); }
  };

  const manualBypass = async () => {
    if (!picked) return;
    if (!bypassReason.trim()) { toast.error("Reason is required for a manual bypass."); return; }
    if (!window.confirm(`Bypass FAA verification and mark ${picked.first_name} verified? Reason: "${bypassReason}"`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${picked.id}/faa-force-verify`, { manual_reason: bypassReason.trim() });
      toast.success(`${picked.first_name} force-verified with note.`);
      setPicked(null); setBypassReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Bypass failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-faa-fixer">
      <Card>
        <div className="eyebrow mb-1">// FAA Fixer</div>
        <div className="text-[12.5px] text-white/60 max-w-3xl">
          Emergency tool for helping a pilot whose standard cross-check fails
          — alternate spellings, married-name changes, or missing from the imported dataset.
        </div>
      </Card>

      {/* Data freshness — last successful import + one-tap re-import */}
      {importStatus && (() => {
        const last = importStatus.last_ok_run;
        const finished = last?.finished_at ? new Date(last.finished_at) : null;
        const ageDays = finished ? Math.floor((Date.now() - finished.getTime()) / (24 * 3600 * 1000)) : null;
        const stale = ageDays === null || ageDays >= 28;
        return (
          <Card testId="faa-fixer-freshness">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className={`eyebrow mb-1 ${stale ? "text-amber-300" : "text-avail"}`}>
                  // FAA Data Freshness {stale ? "· STALE" : "· CURRENT"}
                </div>
                <div className="text-[13px] text-white/85">
                  {finished ? (
                    <>Last imported <span className="text-white font-semibold">{finished.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                    {ageDays !== null && <> · {ageDays} day{ageDays === 1 ? "" : "s"} ago</>}
                    {last?.basic_rows && <> · <span className="mono text-cyan">{last.basic_rows.toLocaleString()}</span> pilots indexed</>}
                    </>
                  ) : "No successful import on record."}
                </div>
                {importStatus.next_scheduled_run_utc && (
                  <div className="text-[11px] text-white/55 mt-1">
                    Next monthly cron:&nbsp;
                    <span className="mono text-white/75">
                      {new Date(importStatus.next_scheduled_run_utc).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short" })}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={importNow}
                disabled={importing}
                data-testid="faa-fixer-import-now"
                className={`mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border flex-shrink-0 ${
                  stale
                    ? "border-amber-400/60 bg-amber-400/10 text-amber-200 hover:bg-amber-400/[0.18]"
                    : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                } disabled:opacity-60`}
              >
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : (
                  <>{stale ? "Import Now (stale)" : "Re-import"} <ArrowRight className="h-3 w-3 inline ml-0.5" /></>
                )}
              </button>
            </div>
          </Card>
        );
      })()}

      {/* Step 1 — pick the user */}
      <Card testId="faa-fixer-user-search">
        <div className="eyebrow mb-2">Step 1 — Find the pilot</div>
        <div className="flex gap-2 items-center">
          <input
            data-testid="faa-fixer-user-input"
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchUsers()}
            placeholder="Search by name, email or nickname (e.g. Vinny, caldara@…)"
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan/50"
          />
          <button onClick={searchUsers} disabled={busy} data-testid="faa-fixer-user-search-btn" className="btn-hud btn-hud-solid">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Search className="h-3.5 w-3.5" /> SEARCH</>}
          </button>
        </div>
        {users.length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid="faa-fixer-user-results">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => setPicked(u)}
                data-testid={`faa-fixer-user-${u.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                  picked?.id === u.id ? "border-cyan/60 bg-cyan/[0.10]" : "border-white/[0.08] bg-white/[0.03] hover:border-cyan/40"
                }`}
              >
                <div className="text-[13px] text-white font-medium">
                  {u.first_name} {u.last_name}{u.nickname ? <span className="text-white/50"> ({u.nickname})</span> : null}
                  {u.faa_verified && <span className="ml-2 mono text-[9px] tracking-widest text-avail border border-avail/40 bg-avail/10 px-1.5 py-0.5 rounded">FAA</span>}
                </div>
                <div className="text-[11px] text-white/50 mt-0.5">
                  {u.email} · <span className="mono">{u.role}</span>
                  {u.identity_legal_first && (u.identity_legal_first !== u.first_name) && (
                    <> · ID legal: <span className="text-cyan">{u.identity_legal_first} {u.identity_legal_last}</span></>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {picked && (
        <>
          {/* Step 2 — FAA search */}
          <Card testId="faa-fixer-faa-search">
            <div className="eyebrow mb-2">Step 2 — Find their FAA record</div>
            <div className="mono text-[11px] text-white/60 mb-3">
              Searching for: <span className="text-white font-semibold">{picked.first_name} {picked.last_name}</span>
              {picked.identity_legal_first && (
                <> · legal name from ID: <span className="text-cyan">{picked.identity_legal_first} {picked.identity_legal_last}</span></>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="faa-fixer-faa-first"
                value={faaFirst}
                onChange={(e) => setFaaFirst(e.target.value)}
                placeholder="FAA first name (partial ok)"
                className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan/50"
              />
              <input
                data-testid="faa-fixer-faa-last"
                value={faaLast}
                onChange={(e) => setFaaLast(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchFaa()}
                placeholder="FAA last name (required, 3+ chars)"
                className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan/50"
              />
            </div>
            <button onClick={searchFaa} disabled={busy || faaLast.trim().length < 3} data-testid="faa-fixer-faa-search-btn" className="btn-hud btn-hud-solid mt-2">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Search className="h-3.5 w-3.5" /> SEARCH FAA</>}
            </button>
            {faaResults && (
              <div className="mt-4 space-y-1.5 max-h-96 overflow-y-auto" data-testid="faa-fixer-faa-results">
                {faaResults.length === 0 && <div className="text-[12.5px] text-amber-300">No FAA records match those criteria.</div>}
                {faaResults.map((m) => (
                  <div key={m.unique_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.03]">
                    <div className="min-w-0">
                      <div className="text-[13px] text-white font-medium">{m.search_first} {m.search_last}</div>
                      <div className="text-[11px] text-white/55 mono">
                        {m.unique_id} · {m.city || "—"}, {m.state || "—"}
                      </div>
                    </div>
                    <button
                      onClick={() => attachMatch(m.unique_id)}
                      disabled={busy}
                      data-testid={`faa-fixer-attach-${m.unique_id}`}
                      className="mono text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded border border-avail/50 text-avail hover:bg-avail/[0.10] flex-shrink-0"
                    >
                      Attach + Verify <ArrowRight className="h-3 w-3 inline ml-0.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Step 3 — Manual bypass fallback */}
          <Card testId="faa-fixer-bypass">
            <div className="eyebrow mb-2">Step 3 (optional) — Manual bypass</div>
            <div className="text-[12.5px] text-white/60 mb-3">
              Can't find them in the FAA data at all? Mark them verified with an audit note (used for legitimate pilots whose FAA data is stale, missing, or under a different legal name that isn't in our imported set).
            </div>
            <input
              data-testid="faa-fixer-bypass-reason"
              value={bypassReason}
              onChange={(e) => setBypassReason(e.target.value)}
              placeholder="Reason (e.g. Confirmed via email + cert photo on 8/6/26)"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan/50"
            />
            <button
              onClick={manualBypass}
              disabled={busy || !bypassReason.trim()}
              data-testid="faa-fixer-bypass-btn"
              className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-amber-400/50 text-amber-300 hover:bg-amber-400/[0.10] mt-2"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : "Force-verify with audit note"}
            </button>
          </Card>
        </>
      )}
    </div>
  );
}


/* ============================================================
 * CREW VERIFICATIONS QUEUE
 * Live list of Flight Attendant (and other cabin-crew) credential
 * submissions with 1-tap approve / reject. Cleiton sat pending 2.5
 * weeks — this queue makes sure no submission ever rots again.
 * ============================================================ */
function CrewVerificationsTab() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/fa-verifications?status=${encodeURIComponent(statusFilter)}`);
      setRows(r.data.submissions || []);
    } catch { toast.error("Failed to load queue"); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const review = async (submission_id, approve) => {
    setReviewing(submission_id);
    try {
      await api.post(`/admin/fa-verifications/${submission_id}/review`, {
        approve,
        review_note: note.trim() || null,
      });
      toast.success(approve ? "Approved — member fully activated." : "Rejected.");
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    } finally { setReviewing(null); }
  };

  const STATUS_TABS = [
    { key: "pending",  label: "Pending",  tone: "amber-300" },
    { key: "approved", label: "Approved", tone: "avail"     },
    { key: "rejected", label: "Rejected", tone: "red-300"   },
  ];

  return (
    <div className="space-y-4" data-testid="admin-crew-verifications">
      <Card>
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div>
            <div className="eyebrow mb-1">// Crew Verification Queue</div>
            <div className="text-[12.5px] text-white/60 max-w-2xl">
              Flight Attendants and other cabin-crew submit credentials here. Approve to flip their <span className="mono">fa_verification_status → verified</span> and their verified badge on. Aim: nothing sits &gt; 48 hrs.
            </div>
          </div>
          <button onClick={load} data-testid="crew-verif-refresh" className="btn-hud">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5" /> REFRESH</>}
          </button>
        </div>
        <div className="flex gap-1.5 mt-3">
          {STATUS_TABS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              data-testid={`crew-verif-filter-${s.key}`}
              className={`mono text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full border transition-colors ${
                statusFilter === s.key
                  ? `text-white border-cyan/60 bg-cyan/[0.10]`
                  : "text-white/60 border-white/15 hover:text-white"
              }`}
            >
              {s.label} {statusFilter === s.key && `(${rows.length})`}
            </button>
          ))}
        </div>
      </Card>

      {statusFilter === "pending" && rows.length > 0 && (
        <Card>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Review note (optional, applied to whichever action you tap next)</div>
          <input
            data-testid="crew-verif-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Approved — welcome aboard, Cleiton"
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan/50"
          />
        </Card>
      )}

      {loading ? (
        <Card><div className="text-[12.5px] text-white/60">Loading…</div></Card>
      ) : rows.length === 0 ? (
        <Card><div className="text-[12.5px] text-white/60">No {statusFilter} submissions.</div></Card>
      ) : (
        <div className="space-y-2" data-testid="crew-verif-list">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`crew-verif-row-${r.id}`}
              className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[14px] text-white font-semibold">
                    {r.first_name} {r.last_name} <span className="text-white/50 font-normal">· {r.email}</span>
                  </div>
                  <div className="mono text-[11px] text-white/55 mt-1 space-x-2">
                    <span>Submitted {(r.created_at || "").slice(0, 10)}</span>
                    {r.years_experience && <span>· {r.years_experience} yrs exp</span>}
                    {r.employer && <span>· @ {r.employer}</span>}
                    {r.certifications?.length ? <span>· {r.certifications.length} certs</span> : null}
                  </div>
                  {r.notes && (
                    <div className="text-[12.5px] text-white/70 mt-2 italic">"{r.notes}"</div>
                  )}
                  {r.employer_name && (
                    <div className="mono text-[11px] text-white/60 mt-1">Employer: <span className="text-white/85">{r.employer_name}</span></div>
                  )}
                  {r.doc_type && (
                    <div className="mono text-[10px] text-white/50 mt-1">Doc type: <span className="text-white/75">{r.doc_type}</span></div>
                  )}
                  {r.ai_verdict && (
                    <div className="mt-2 rounded-md border border-white/10 bg-white/[0.03] p-2 text-[11px]">
                      <div className="mono text-[10px] text-cyan tracking-widest uppercase mb-1">// AI Verdict · conf {(r.ai_verdict.confidence || 0).toFixed(2)}</div>
                      <div className="text-white/80">
                        <span className="text-white/60">Issuer:</span> {r.ai_verdict.issuer || "—"}
                        {r.ai_verdict.expires ? <> · <span className="text-white/60">Expires:</span> {r.ai_verdict.expires}</> : null}
                      </div>
                      {r.ai_verdict.reason && <div className="text-white/60 italic mt-0.5">"{r.ai_verdict.reason}"</div>}
                      {r.ai_verdict.red_flags?.length ? (
                        <div className="text-amber-300 mt-0.5">Flags: {r.ai_verdict.red_flags.join(", ")}</div>
                      ) : null}
                    </div>
                  )}
                  {r.file_url && (
                    <div className="mt-3">
                      <DocPreview
                        url={`${API_BASE}${r.file_url}`}
                        contentType={r.file_content_type}
                        filename={r.file_original_name}
                      />
                    </div>
                  )}
                </div>
                {statusFilter === "pending" && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      disabled={reviewing === r.id}
                      onClick={() => review(r.id, false)}
                      data-testid={`crew-verif-reject-${r.id}`}
                      className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-500/50 bg-red-500/[0.06] text-red-300 hover:bg-red-500/[0.14] disabled:opacity-60"
                    >
                      {reviewing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reject"}
                    </button>
                    <button
                      disabled={reviewing === r.id}
                      onClick={() => review(r.id, true)}
                      data-testid={`crew-verif-approve-${r.id}`}
                      className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-avail/60 bg-avail/[0.14] text-avail hover:bg-avail/[0.24] disabled:opacity-60 font-bold"
                    >
                      {reviewing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Approve ✓"}
                    </button>
                  </div>
                )}
                {statusFilter !== "pending" && (
                  <div className="mono text-[10px] tracking-widest uppercase text-white/55">
                    {r.status === "approved" ? "✓ Approved" : "✗ Rejected"} {r.reviewed_at ? `· ${r.reviewed_at.slice(0,10)}` : ""}
                    {r.review_note && <div className="text-[11px] text-white/45 mt-1 italic">"{r.review_note}"</div>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
 * DocPreview + MemberDocsPanel
 * Inline preview for admin verification. Images render as thumbs,
 * PDFs get a "// PDF" tile + open-in-tab link, videos get a <video>.
 * ============================================================ */

const KIND_LABEL = {
  fa_verification: "FA Credential",
  identity: "ID / Passport",
  identity_selfie: "ID Selfie",
  avatar: "Avatar",
  aircraft_photo: "Aircraft photo",
  paystub: "Pay stub",
  phf: "PHF form",
  misc: "Misc",
};

function DocPreview({ url, contentType, filename }) {
  const ct = (contentType || "").toLowerCase();
  const isImage = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";
  const isVideo = ct.startsWith("video/");
  return (
    <div className="rounded-lg border border-cyan/25 bg-cyan/[0.03] p-2">
      <div className="mono text-[10px] text-cyan tracking-widest uppercase mb-1.5 flex items-center justify-between gap-2">
        <span className="truncate">// {filename || "uploaded document"}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-cyan hover:text-white inline-flex items-center gap-1"
          data-testid="doc-preview-open"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      {isImage && (
        <a href={url} target="_blank" rel="noreferrer" className="block">
          <img
            src={url}
            alt={filename || "document"}
            className="w-full max-h-[420px] object-contain rounded-md bg-black/40"
            data-testid="doc-preview-img"
          />
        </a>
      )}
      {isPdf && (
        <object data={url} type="application/pdf" className="w-full h-[420px] rounded-md bg-black/40" data-testid="doc-preview-pdf">
          <div className="p-6 text-center text-[12.5px] text-white/60">
            PDF can't render inline here — <a href={url} target="_blank" rel="noreferrer" className="text-cyan underline">open in a new tab</a>.
          </div>
        </object>
      )}
      {isVideo && (
        <video src={url} controls className="w-full max-h-[420px] rounded-md bg-black/40" data-testid="doc-preview-video" />
      )}
      {!isImage && !isPdf && !isVideo && (
        <div className="p-4 text-[12.5px] text-white/60">Preview not available for {ct || "this file type"}. Open link above.</div>
      )}
    </div>
  );
}

function MemberDocsPanel({ userId }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // file id currently previewed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${userId}/files`);
      setFiles(r.data.files || []);
    } catch {
      toast.error("Couldn't load member documents");
      setFiles([]);
    } finally { setLoading(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 pt-4 border-t border-white/[0.08]" data-testid="member-docs-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">// Uploaded Documents {files ? `(${files.length})` : ""}</div>
        <button onClick={load} className="mono text-[10px] tracking-widest uppercase text-white/55 hover:text-white inline-flex items-center gap-1" data-testid="member-docs-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      {loading && !files ? (
        <div className="text-[12.5px] text-white/50">Loading…</div>
      ) : files && files.length === 0 ? (
        <div className="text-[12.5px] text-white/50">This member hasn't uploaded any documents yet.</div>
      ) : (
        <div className="space-y-2">
          {(files || []).map((f) => {
            const isOpen = expanded === f.id;
            const kb = f.size ? Math.round(f.size / 1024) : 0;
            const kindLabel = KIND_LABEL[f.kind] || f.kind || "file";
            const fullUrl = `${API_BASE}${f.url}`;
            return (
              <div key={f.id} className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-testid={`member-doc-${f.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge tone="sky">{kindLabel}</Badge>
                      <span className="text-[12.5px] text-white/85 truncate">{f.original_filename || "(unnamed)"}</span>
                    </div>
                    <div className="mono text-[10px] text-white/45 mt-0.5">
                      {f.content_type || "—"} · {kb ? `${kb.toLocaleString()} KB · ` : ""}{(f.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => setExpanded(isOpen ? null : f.id)}
                      className="mono text-[10px] tracking-widest uppercase text-cyan border border-cyan/40 bg-cyan/[0.06] px-2 py-1 rounded hover:bg-cyan/[0.14]"
                      data-testid={`member-doc-toggle-${f.id}`}
                    >
                      {isOpen ? "Hide" : "View"}
                    </button>
                    <a
                      href={fullUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mono text-[10px] tracking-widest uppercase text-white/60 border border-white/15 px-2 py-1 rounded hover:bg-white/5 inline-flex items-center gap-1"
                      data-testid={`member-doc-open-${f.id}`}
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-2">
                    <DocPreview url={fullUrl} contentType={f.content_type} filename={f.original_filename} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ============================================================
 * DigitalIdPanel — shows whether the member has paid for or been
 * granted the $199 PJP Digital ID / Crew ID add-on. Sourced from
 * user.pjp_id_status + pjp_id_grant_source. No API call needed —
 * everything comes back on the /admin/members payload.
 * ============================================================ */
function DigitalIdPanel({ member }) {
  const status = member.pjp_id_status || "none";
  const active = status === "active";
  const grantSrc = member.pjp_id_grant_source || "";
  const expires = member.pjp_id_expires_at;
  const token = member.pjp_id?.public_token;
  const founding = !!member.founding_member;

  const isGrant = active && (founding || grantSrc.includes("founding") || grantSrc.includes("migration") || grantSrc.includes("comp"));
  const label = !active
    ? "Not purchased"
    : isGrant
    ? "Granted (comp)"
    : "Paid ($199)";
  const tone = !active ? "neutral" : isGrant ? "gold" : "avail";

  return (
    <div className="mt-4 pt-4 border-t border-white/[0.08]" data-testid="member-digital-id-panel">
      <div className="eyebrow mb-2">// PJP Digital ID · Crew ID</div>
      <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge tone={tone}>{label.toUpperCase()}</Badge>
            {active && (
              <span className="mono text-[10px] text-white/50 tracking-widest uppercase">
                Status: <span className="text-white/85">{status}</span>
              </span>
            )}
          </div>
          {active && token && (
            <a
              href={`/id/${token}`}
              target="_blank"
              rel="noreferrer"
              className="mono text-[10px] tracking-widest uppercase text-cyan border border-cyan/40 bg-cyan/[0.06] px-2 py-1 rounded hover:bg-cyan/[0.14] inline-flex items-center gap-1"
              data-testid="member-digital-id-link"
            >
              View badge <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        {!active && (
          <div className="mono text-[11px] text-white/55 mt-2">
            This member has <span className="text-amber-300">not purchased</span> the Digital ID ($199 add-on) and has no comp grant.
          </div>
        )}
        {active && (
          <div className="mono text-[11px] text-white/60 mt-2 space-y-0.5">
            {grantSrc && <div>Source: <span className="text-white/85">{grantSrc}</span></div>}
            {expires && <div>Expires: <span className="text-white/85">{String(expires).slice(0, 10)}</span></div>}
            {founding && <div>Founding member: <span className="text-gold">✓</span></div>}
            {!token && <div className="text-white/45 italic">Purchased but hasn't enabled/activated the badge yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
